import { useEffect, useRef } from 'react';

/**
 * Budget for an express wallet to report its readiness signal before we treat
 * silence as "unavailable".
 *
 * The PSPs all resolve quickly in practice (Stripe ECE `onReady`, Adyen
 * `isAvailable()`), but a hung SDK load / blocked iframe / device quirk can
 * leave the callback unfired forever. For templates that hide the wallet
 * behind a skeleton until ready (`deferExpressWalletReveal`), that would strand
 * the player on a permanent skeleton — so after this budget we force the
 * template's normal unavailable-fallback path.
 */
export const EXPRESS_WALLET_READY_TIMEOUT_MS = 4000;

/**
 * Budget for Stripe ECE to fire `onReady` / `onLoadError` AFTER Stripe.js is
 * already resolved and the element is mounted. Cold Android ECE boots can take
 * several seconds (see `EXPRESS_ELEMENTS_BASE_OPTIONS` notes), so this is
 * intentionally looser than {@link EXPRESS_WALLET_READY_TIMEOUT_MS} — hang
 * breaker, not a tight probe.
 */
export const EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS = 10000;

/**
 * RUM `reason` when {@link EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS} fires.
 * This is OUR client-side hang-breaker — Stripe never emits a "timeout"
 * for a silent ECE; do not conflate with Stripe `onLoadError` / SDK errors.
 */
export const EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON = 'client_ece_hang_timeout';

/**
 * Last-resort budget owned by the `deferExpressWalletReveal` skeleton gate
 * ITSELF, rather than by the wallet form under it.
 *
 * Every breaker above is armed by a form that has to MOUNT to arm it, so any
 * path where the expected form never mounts — an unforeseen provider/flag
 * combination, a slot that renders a substitute button, a child that throws
 * before its effects run — leaves the gate with nobody to lift it and strands
 * the player on a permanent skeleton. This budget belongs to the gate owner so
 * it holds regardless of what renders inside.
 *
 * Deliberately looser than every in-slot breaker (ECE's 10s is the loosest) so
 * it can only ever fire AFTER the wallet's own timeout had its chance —
 * otherwise it would swap the template away from a wallet that was merely slow.
 */
export const EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS = 12000;

/** RUM `reason` when {@link EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS} fires. */
export const EXPRESS_WALLET_GATE_HANG_TIMEOUT_REASON = 'client_gate_hang_timeout';

/**
 * Fires `onTimeout` once if it isn't cancelled within `timeoutMs`.
 *
 * The timer only runs while `enabled` is true; flipping `enabled` to false
 * (e.g. when the wallet resolves to `ready`/`unavailable`) clears it via the
 * effect cleanup, and so does unmount. `onTimeout` is read through a ref so a
 * changing callback identity doesn't restart the timer.
 */
export const useExpressReadyTimeout = (
  enabled: boolean,
  onTimeout: () => void,
  timeoutMs: number = EXPRESS_WALLET_READY_TIMEOUT_MS
): void => {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) return;
    const id = window.setTimeout(() => onTimeoutRef.current(), timeoutMs);
    return () => window.clearTimeout(id);
  }, [enabled, timeoutMs]);
};
